import i18n, { TOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import Backend, { HttpBackendOptions, RequestCallback } from "i18next-http-backend";
import axios from "axios";
import { getApiUrl } from "src/api/axiosClient/utils";
import { getEnv } from "src/utils/getEnv";
import { LanguageService } from "src/services/LanguageService";
import { mergeTranslationResources } from "src/i18nResources";

export const defaultNS = "default_ns";

const isDevelopment = (): boolean => {
    try {
        return getEnv("VITE_NODE_ENV") === "development";
    } catch {
        return false;
    }
};

const loadStaticFallback = async (language: string): Promise<Record<string, string>> => {
    try {
        const locale = await import(`../public/locales/${language}/default_ns.json`);
        return locale.default || locale;
    } catch {
        return {};
    }
};

const fallbackLng = "en"; // Standard W3C fallback

const stripToTranslatePostProcessor = {
    type: "postProcessor" as const,
    name: "stripToTranslate",
    process(value: string, _key: string, options: Record<string, unknown>, _translator: unknown): string {
        if (typeof value !== "string" || !value.includes("[TO_TRANSLATE]")) {
            return value;
        }

        const cleaned = value.replace(/^\[TO_TRANSLATE\]\s*/, "").trim();
        if (cleaned === "" || cleaned === "[TO_TRANSLATE]") {
            return typeof options.defaultValue === "string" ? options.defaultValue : value;
        }

        return cleaned;
    },
};

const translationApiClient = axios.create({
    baseURL: getApiUrl(),
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
    timeout: 5000,
});

i18n.use(Backend)
    .use(stripToTranslatePostProcessor)
    .use(initReactI18next)
    .init({
        lng: LanguageService.getCurrentLanguage() || "en",
        fallbackLng: fallbackLng,
        ns: ["default_ns"],
        defaultNS,
        backend: {
            loadPath: "/api/translations/{{lng}}",
            parse: (data: string) => {
                try {
                    return mergeTranslationResources({}, JSON.parse(data));
                } catch {
                    return {};
                }
            },
            request: (options: HttpBackendOptions, url: string, payload: string, callback: RequestCallback) => {
                const language = url.split("/").pop() || "en";

                translationApiClient
                    .get(`/api/translations/${language}`)
                    .then(async (response) => {
                        const apiTranslations = mergeTranslationResources({}, response.data);
                        if (Object.keys(apiTranslations).length === 0) {
                            throw new Error(`Empty translations payload for language: ${language}`);
                        }

                        const staticBase = await loadStaticFallback(language);
                        const translations = mergeTranslationResources(staticBase, apiTranslations);

                        callback(null, {
                            status: response.status,
                            data: JSON.stringify(translations),
                        });
                    })
                    .catch(async () => {
                        const staticFallback = await loadStaticFallback(language);
                        if (staticFallback && Object.keys(staticFallback).length > 0) {
                            callback(null, {
                                status: 200,
                                data: JSON.stringify(staticFallback),
                            });
                        } else {
                            const errorMessage = `No static fallback available for language: ${language}`;

                            if (process.env.NODE_ENV === "development") {
                                callback(null, {
                                    status: 200,
                                    data: JSON.stringify({
                                        __DEV_MISSING_LOCALE__: language,
                                        __DEV_ERROR__: errorMessage,
                                    }),
                                });
                            } else {
                                if (typeof globalThis !== "undefined" && (globalThis as any).Sentry) {
                                    (globalThis as any).Sentry.captureException(new Error(errorMessage), {
                                        tags: { component: "i18n-fallback" },
                                        extra: {
                                            language,
                                            timestamp: new Date().toISOString(),
                                        },
                                    });
                                }

                                callback(null, {
                                    status: 200,
                                    data: "{}",
                                });
                            }
                        }
                    });
            },
        },
        nsSeparator: false,
        keySeparator: false,
        interpolation: {
            escapeValue: false,
        },
        partialBundledLanguages: false,
        load: "languageOnly",
        postProcess: ["stripToTranslate"],
        saveMissing: true,
        missingKeyHandler: (
            lngs: readonly string[],
            ns: string,
            key: string,
            fallbackValue: string,
            updateMissing: boolean,
            options: TOptions,
        ) => {
            if (isDevelopment()) {
                // TODO: Implement a missing key reporting endpoint here
            }
        },
    });

export default i18n;
